/**
 * WebUI IoC container keys for dependency injection.
 *
 * Use with `useInject` hook to resolve dependencies:
 * ```tsx
 * import { useInject, WebuiIocKey } from '@ztechapps/webui.contract'
 * import type { RouteDef } from '@ztechapps/common.contract'
 *
 * const orderRoute = useInject<RouteDef>(WebuiIocKey.routes.orderItem)
 * const linkTo = orderRoute.altPath({ _id: orderId })
 * ```
 *
 * **Never hardcode route paths** - always inject via `WebuiIocKey.routes.*`
 *
 * @see docs/ai/rules/webui-hook-style-guide.md for usage patterns
 */
export const WebuiIocKey = {
  /** Cross-module product components */
  product: {
    productSelect: Symbol.for('product.productSelect'),
    productSelectCreatable: Symbol.for('product.productSelectCreatable'),
    productQuantity: Symbol.for('product.productQuantity'),
    productWidget: Symbol.for('product.productWidget'),
  },

  /** Cross-module order components */
  order: {
    /** Order async-select (status dot + code + company + order date). Bound to OrderSelect. */
    orderSelect: Symbol.for('order.orderSelect'),
    /** Compact order code link + hover tooltip (EntityCodeLink with the order preview). */
    orderCodeLink: Symbol.for('order.orderCodeLink'),
    /** Display-only order widget (status badge + linkable code + summary + payment badge + PO#). Bound to OrderWidget. */
    orderWidget: Symbol.for('order.orderWidget'),
    /** Conversation-header button that creates a sales order from the open conversation. Bound to CreateOrderFromConversationButton. */
    createOrderFromConversationButton: Symbol.for('order.createOrderFromConversationButton'),
  },

  /** Cross-module work order components */
  workOrder: {
    createAndBuild: Symbol.for('workOrder.createAndBuild'),
  },

  /** Cross-module adjustment components */
  adjustment: {
    adjustment: Symbol.for('adjustment.adjustment'),
  },

  /** Cross-module purchase order components */
  purchaseOrder: {
    purchaseOrderSelect: Symbol.for('purchaseOrder.purchaseOrderSelect'),
  },

  /** Cross-module project components */
  project: {
    projectSelect: Symbol.for('project.projectSelect'),
  },

  /** Cross-module vendor components */
  vendor: {
    vendorSelect: Symbol.for('vendor.vendorSelect'),
    vendorInfoForm: Symbol.for('vendor.vendorInfoForm'),
  },

  /**
   * Cross-module company components.
   *
   * `companySelect` is bound to a no-op fallback by every `Container` (see
   * `Container.ts`'s constructor) so `UpdateProfile` — which renders a
   * "Company" field unconditionally on every admin app — resolves cleanly
   * even where no company concept exists (QuickScore, OwnText, GateKeeper).
   * `libs/company/webui/src/container.ts`'s `companyContainer` binds the
   * real `CompanySelect` over this fallback wherever it is loaded (only
   * `panelpunches/admin` today).
   */
  company: {
    companySelect: Symbol.for('company.companySelect'),
  },

  /** Cross-module customer components */
  customer: {
    customerSelect: Symbol.for('customer.customerSelect'),
    customerSelectCreatable: Symbol.for('customer.customerSelectCreatable'),
    customerAddress: Symbol.for('customer.customerAddress'),
    customerWidget: Symbol.for('customer.customerWidget'),
    customerInfoForm: Symbol.for('customer.customerInfoForm'),
    contactSelect: Symbol.for('customer.contactSelect'),
  },

  /** Cross-module chat components */
  chat: {
    conversationTemplateVariablesProvider: Symbol.for('chat.conversationTemplateVariablesProvider'),
  },

  /**
   * Cross-module AI chat tool result renderers.
   *
   * Each key resolves to a React component keyed by the backend tool name
   * (e.g. `searchProducts`, `searchBlogs`). `ToolCallDisplay` uses
   * `useInjectComponent` to look up a renderer for the current tool and, if
   * one is bound, renders it in place of the default JSON/collapsible UI.
   *
   * `unknown` is the fallback sentinel — bound to a no-op component in
   * `libs/ai/webui/src/container.ts` so tools without a dedicated renderer
   * resolve cleanly through the same `useInjectComponent` path instead of
   * throwing "No binding found" on every render.
   */
  ai: {
    chatRenderer: {
      searchProducts: Symbol.for('ai.chatRenderer.searchProducts'),
      searchBlogs: Symbol.for('ai.chatRenderer.searchBlogs'),
      createOrder: Symbol.for('ai.chatRenderer.createOrder'),
      createPurchaseOrder: Symbol.for('ai.chatRenderer.createPurchaseOrder'),
      createProduct: Symbol.for('ai.chatRenderer.createProduct'),
      buildPivotLayout: Symbol.for('ai.chatRenderer.buildPivotLayout'),
      switchPayrollPeriod: Symbol.for('ai.chatRenderer.switchPayrollPeriod'),
      unknown: Symbol.for('ai.chatRenderer.__unknown__'),
    },
  },

  /**
   * Route definitions for cross-module navigation.
   * Inject as `RouteDef` type and use `.altPath({ _id })` for URL generation.
   */
  routes: {
    /** Product detail page: /admin/inventory/products/:id */
    productItem: Symbol.for('routes.productItem'),
    /** Order detail page: /admin/sales/orders/:id */
    orderItem: Symbol.for('routes.orderItem'),
    /** Purchase order detail page: /admin/purchasing/purchase-orders/:id */
    purchaseOrderItem: Symbol.for('routes.purchaseOrderItem'),
    /** Customer contact detail page: /admin/crm/contacts/:id */
    customerContactItem: Symbol.for('routes.customerContactItem'),
    /** Customer company detail page: /admin/crm/companies/:id */
    customerCompanyItem: Symbol.for('routes.customerCompanyItem'),
    /** Company detail page: /app/company/:id */
    companyItem: Symbol.for('routes.companyItem'),
    /** Vendor contact detail page: /admin/purchasing/vendors/:id */
    vendorContactItem: Symbol.for('routes.vendorContactItem'),
    /** Project detail page: /admin/projects/:id */
    projectItem: Symbol.for('routes.projectItem'),
    /** Project list page: /admin/projects */
    projectList: Symbol.for('routes.projectList'),
    /** Work order detail page: /admin/production/work-orders/:id */
    workOrderItem: Symbol.for('routes.workOrderItem'),
    /** Work order list page: /admin/production/work-orders */
    workOrderList: Symbol.for('routes.workOrderList'),
    /** Task detail page: /admin/projects/tasks/:id */
    taskItem: Symbol.for('routes.taskItem'),
    /** Logistic detail page: /admin/logistics/:id */
    logisticItem: Symbol.for('routes.logisticItem'),
    /** Logistic list page: /admin/logistics */
    logisticList: Symbol.for('routes.logisticList'),
    /** Adjustment detail page: /admin/inventory/adjustments/:id */
    adjustmentItem: Symbol.for('routes.adjustmentItem'),
    /** Adjustment list page: /admin/inventory/adjustments */
    adjustmentList: Symbol.for('routes.adjustmentList'),
    /** Conversation detail page: /app/conversation/detail/:_id */
    conversationItem: Symbol.for('routes.conversationItem'),
    /** Blog list page: /app/settings/blogs */
    blogList: Symbol.for('routes.blogList'),
    /** Blog detail page: /app/settings/blogs/:_id */
    blogItem: Symbol.for('routes.blogItem'),
    /** Standalone blog preview page: /blog-preview/:slug */
    blogPreview: Symbol.for('routes.blogPreview'),
  },
} as const
